import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { platformAiSettingsKeys } from '@/api/query-keys';
import type {
  PlatformAiSettings,
  ShowPlatformAiSettingsResponse,
  UpdatePlatformAiSettingsRequest,
  UpdatePlatformAiSettingsResponse
} from '@bliks-io/contracts';

/**
 * Fetch global platform AI settings (superadmin only)
 */
export function usePlatformAiSettings() {
  return useQuery({
    queryKey: platformAiSettingsKeys.all,
    queryFn: async ({ signal }): Promise<PlatformAiSettings> => {
      const { data } = await apiClient.get<ShowPlatformAiSettingsResponse>(
        '/api/v2/platform/ai-settings',
        { signal }
      );
      return data.data;
    }
  });
}

/**
 * Update global platform AI settings (superadmin only)
 */
export function useUpdatePlatformAiSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: UpdatePlatformAiSettingsRequest): Promise<PlatformAiSettings> => {
      const { data } = await apiClient.patch<UpdatePlatformAiSettingsResponse>(
        '/api/v2/platform/ai-settings',
        updates
      );
      return data.data;
    },
    onSuccess: (data) => {
      // Write the canonical response straight into the cache so the form
      // re-renders with the new values atomically when pending edits are
      // cleared. Invalidating instead would trigger a refetch and leave the
      // UI briefly showing the pre-save state — a visible flash.
      queryClient.setQueryData(platformAiSettingsKeys.all, data);
    }
  });
}
